/** Export builders. Core owns text/CSS/JSON tokens; the frontend adds the
 *  usage guide, labelled PNG specimen, sources/evidence manifest, and project
 *  file. Every format discloses exactly what it contains. */

import {
  createProjectFile,
  exportCssCustomProperties,
  exportDesignTokens,
  exportPlainTextStyleSheet,
  serializeProjectFile,
  type DirectionCard,
  type EntryRoute,
  type TypeSetProjectFile,
  type TypeAnswers,
  type TypePath,
} from "@core/index.js";

import candidateFonts from "@data/legacy/type-set/data/candidate-fonts.json";

import { stories } from "./catalog";
import { typeLibrary } from "./typeLibrary";

export const buildCoreExports = (direction: DirectionCard) => ({
  plainText: exportPlainTextStyleSheet(direction),
  css: exportCssCustomProperties(direction),
  tokens: exportDesignTokens(direction),
});

export const exportDisplayName = (direction: DirectionCard, route: EntryRoute): string => {
  if (direction.name.trim() && direction.name !== "Untitled direction") return direction.name;
  const type = direction.type.recommendations.find((item) => item.systemId === direction.selected.typeSystemId) ?? null;
  return type?.name ?? "Type direction";
};

export const buildReadableSettings = (direction: DirectionCard, route: EntryRoute): string => {
  const type = direction.type.recommendations.find((item) => item.systemId === direction.selected.typeSystemId) ?? null;
  const lines: string[] = [`TYPE SET — ${exportDisplayName(direction, route)}`, ""];
  lines.push("TYPE DIRECTION");
  if (type) {
    lines.push(type.name, type.reason);
    for (const [role, label] of [["display", "Headlines"], ["body", "Body copy"], ["utility", "Labels and data"]] as const) {
      const fontId = type.roles[role].fontId;
      const record = fontRecordFor(fontId);
      lines.push(`${label}: ${record?.family ?? fontId}`);
    }
    lines.push(`Do not: ${type.oneThingNotToDo}`);
    const sourceIds = [...new Set(Object.values(type.roles).map((role) => role.fontId))];
    lines.push("", "OFFICIAL FONT SOURCES");
    for (const fontId of sourceIds) {
      const record = fontRecordFor(fontId);
      lines.push(`- ${record?.family ?? fontId}: ${record?.sourceRepository ?? "Source not recorded"}`);
    }
    lines.push("", "KEEP IN MIND", "- Test the exact files in the application that will build the deck. Browsers are not PowerPoint wearing a little hat.");
  } else {
    lines.push("No honest type recommendation yet.");
    for (const action of direction.type.nextActions) lines.push(`- ${action}`);
  }
  lines.push(
    "",
    "ABOUT THIS FILE",
    "This is a complete Type Set direction.",
    "The fitting was made locally in your browser. Test installation, editable handoff, and the final words before final handoff.",
    "",
  );
  return lines.join("\n");
};

type FontRecord = { id: string; family: string; designer?: string; licence?: { spdx?: string }; sourceRepository?: string };

const fontRecords = (candidateFonts as unknown as { fonts: FontRecord[] }).fonts;
const fontRecordFor = (fontId: string): FontRecord | undefined => {
  const exact = typeLibrary.fonts.find((font) => font.id === fontId);
  if (exact) return {
    id: exact.id,
    family: exact.family,
    designer: exact.designer,
    licence: { spdx: exact.license.spdx },
    sourceRepository: exact.source.upstreamRepository ?? exact.source.metadataUrl,
  };
  return fontRecords.find((font) => font.id === fontId);
};

export const buildEvidenceManifest = (direction: DirectionCard, exportedAt: string): string => {
  const lines: string[] = [
    "# Type Set — sources and evidence manifest",
    "",
    `Project: ${direction.name} (${direction.projectId})`,
    `Exported: ${exportedAt}`,
    `Type truth: ${direction.truth.typeStatus}`,
    "",
    "## Type — exact browser records",
  ];
  for (const item of direction.type.recommendations) {
    lines.push(`- ${item.name} (${item.systemId}) — status: ${item.status}; role: ${item.answerRole}`);
    for (const fontId of [...new Set(Object.values(item.roles).map((role) => role.fontId))]) {
      const record = fontRecordFor(fontId);
      const story = stories.find((entry) => entry.fontId === fontId);
      lines.push(
        `  - ${record?.family ?? fontId}: designer ${record?.designer ?? "not recorded"}; license ${record?.licence?.spdx ?? "unknown"}; source ${record?.sourceRepository ?? "unknown"}${story ? `; story reviewed ${story.reviewedOn}` : ""}`,
      );
    }
  }
  lines.push("", "## Claims and limits");
  lines.push("- Every displayed family resolves to a checked local font file in this build.");
  lines.push("- Browser rendering proves the web specimen, not installation or layout fidelity in another application.");
  lines.push("- Test the final words, editable handoff, and export path before delivery.");
  lines.push(
    "",
    "## Art and copy",
    "- Interface art: Type Set production family by pitch.dog.",
    "- Copy surface: src/content/copy.ts and the exact Type studio interface.",
    "- No font binaries are included in any export. Font IDs are manifest keys, not files.",
    "",
  );
  return lines.join("\n");
};

export const buildProjectFile = (input: {
  direction: DirectionCard;
  typeAnswers: TypeAnswers;
  typePath: TypePath;
  revision?: number;
}): TypeSetProjectFile =>
  createProjectFile({
    direction: input.direction,
    typeAnswers: input.typeAnswers,
    typePath: input.typePath,
    createdAt: new Date().toISOString(),
    ...(input.revision !== undefined ? { revision: input.revision } : {}),
  });

export const serializeProject = serializeProjectFile;

/** The only localStorage key this app may ever write — and only after the
 *  user explicitly asks us to remember the project on this device. */
export const REMEMBERED_PROJECT_KEY = "type-set.remembered-project";

/** Labelled 16:9 type specimen using the same exact browser font stacks as
 * the live preview. Application install/export remains a separate gate. */
export const renderSpecimenPng = async (input: {
  direction: DirectionCard;
  route: EntryRoute;
  displayStack: string;
  bodyStack: string;
}): Promise<Blob> => {
  const { direction } = input;
  const type = direction.type.recommendations.find((item) => item.systemId === direction.selected.typeSystemId) ?? null;
  const canvas = document.createElement("canvas");
  canvas.width = 1200;
  canvas.height = 675;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas unavailable for the specimen export.");

  const background = "#f7f5ef";
  const ink = "#232327";
  const muted = "#55555c";
  const accent = "#2b4ad8";

  context.fillStyle = background;
  context.fillRect(0, 0, 1200, 675);

  context.fillStyle = muted;
  context.font = "600 20px ui-monospace, Menlo, monospace";
  const specimenLabel = "TYPE SET — EXACT BROWSER TYPE PREVIEW";
  context.fillText(specimenLabel, 64, 74);

  context.fillStyle = ink;
  const title = direction.previewCopy.deckTitle ?? exportDisplayName(direction, input.route);
  const titleSize = title.length > 56 ? 42 : title.length > 34 ? 48 : 60;
  context.font = `560 ${titleSize}px ${input.displayStack}`;
  const words = title.trim().split(/\s+/);
  const titleLines: string[] = [];
  let line = "";
  for (const word of words) {
    if (context.measureText(word).width > 1000) {
      if (line) {
        titleLines.push(line);
        line = "";
      }
      let chunk = "";
      for (const character of word) {
        if (chunk && context.measureText(`${chunk}${character}`).width > 1000) {
          titleLines.push(chunk);
          chunk = character;
        } else {
          chunk += character;
        }
      }
      line = chunk;
      continue;
    }
    const candidate = line ? `${line} ${word}` : word;
    if (context.measureText(candidate).width <= 1000 || !line) {
      line = candidate;
    } else {
      titleLines.push(line);
      line = word;
    }
  }
  if (line) titleLines.push(line);
  const lineHeight = Math.round(titleSize * 1.08);
  titleLines.forEach((titleLine, index) => context.fillText(titleLine, 64, 150 + index * lineHeight));

  context.fillStyle = accent;
  const accentY = 160 + titleLines.length * lineHeight;
  context.fillRect(64, accentY, 180, 5);

  context.fillStyle = ink;
  context.font = `400 24px ${input.bodyStack}`;
  const subtitle = direction.previewCopy.subtitle ?? "A type direction in exact browser font files. Test the deck application separately.";
  context.fillText(subtitle, 64, accentY + 55, 1000);

  const specimenY = Math.max(365, accentY + 96);
  context.strokeStyle = "#c9c6bd";
  context.lineWidth = 2;
  context.strokeRect(64, specimenY, 1072, 154);
  context.beginPath();
  context.moveTo(460, specimenY);
  context.lineTo(460, specimenY + 154);
  context.moveTo(845, specimenY);
  context.lineTo(845, specimenY + 154);
  context.stroke();
  context.fillStyle = muted;
  context.font = "600 14px ui-monospace, Menlo, monospace";
  context.fillText("DISPLAY", 84, specimenY + 28);
  context.fillText("BODY", 480, specimenY + 28);
  context.fillText("UTILITY", 865, specimenY + 28);
  context.fillStyle = ink;
  context.font = `600 70px ${input.displayStack}`;
  context.fillText("Aa", 84, specimenY + 114);
  context.font = `400 22px ${input.bodyStack}`;
  context.fillText("The useful bit stays", 480, specimenY + 76, 330);
  context.fillText("easy to read.", 480, specimenY + 108, 330);
  context.font = `500 22px ${input.bodyStack}`;
  context.fillText("Q3 · 48% · 07", 865, specimenY + 89, 245);

  context.fillStyle = muted;
  context.font = "400 16px ui-sans-serif, system-ui, sans-serif";
  const typeLine = type
    ? `Type: ${type.name} — exact in this browser; deck application proof is not included.`
    : "Type needs more information before an honest direction is possible.";
  context.fillText(typeLine, 64, 620);

  // Repaint the small title last so a long display line can never obscure it.
  context.fillStyle = muted;
  context.font = "600 20px ui-monospace, Menlo, monospace";
  context.fillText(specimenLabel, 64, 74);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("PNG encoding failed."))), "image/png");
  });
};

/** Download helper: object URL created and revoked around a single click. */
export const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const downloadText = (text: string, filename: string, mime = "text/plain"): void => {
  downloadBlob(new Blob([text], { type: `${mime};charset=utf-8` }), filename);
};

export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};
